import React from 'react';
import PropTypes from 'prop-types';
import jstz from 'jstz';

import DatePicker from 'common/components/datepicker';
import Select from 'common/components/select';
import ConfirmationModal from 'common/components/confirmation-modal';
import {formatDate, formatDateRange} from 'common/formatters/date-formatter';
import {DateTimeFormat} from 'common/proxies/enum-proxy';
import {capitalize} from 'common/utils/string-util';
import {triggerQueryParams} from 'common/utils/url-util';
import {
  CUSTOM_OPTION_LABEL,
  CUSTOM_OPTION_VALUE,
  NO_PERIOD_OPTION_LABEL,
  NO_PERIOD_OPTION_VALUE,
} from './constants';
import {t} from 'common/utils/translation-util';

import './index.scss';

const TODAY = new Date();

export default class PeriodSelector extends React.Component {
  static propTypes = {
    end: PropTypes.instanceOf(Date),
    groups: PropTypes.object,
    includeCustomPeriod: PropTypes.bool,
    includeEmptyPeriod: PropTypes.bool,
    includeTimezone: PropTypes.bool,
    initialSelection: PropTypes.string,
    onSave: PropTypes.func,
    // ReportingPeriodEnum objects' values:
    options: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        slug: PropTypes.string,
        displayName: PropTypes.string,
        group: PropTypes.string,
        disabled: PropTypes.bool,
      }),
    ),
    start: PropTypes.instanceOf(Date),
  };

  static defaultProps = {
    end: TODAY,
    groups: {},
    includeCustomPeriod: true,
    includeEmptyPeriod: true,
    includeTimezone: false,
    initialSelection: '',
    onSave: defaultOnSave,
    options: [],
    start: TODAY,
  };

  constructor(props) {
    super(props);

    this.state = {
      end: props.end,
      customOptionSelectedDisplay: this.getCustomOptionSelectedDisplay(
        props.start,
        props.end,
      ),
      selected: props.initialSelection,
      start: props.start,
      tz: props.includeTimezone ? jstz.determine().name() : null,
    };

    this.onOptionClick = this.onOptionClick.bind(this);
    this.onChangeStart = this.onChangeStart.bind(this);
    this.onChangeEnd = this.onChangeEnd.bind(this);
    this.onCustomPeriodSave = this.onCustomPeriodSave.bind(this);
    this.onCustomPeriodCancel = this.onCustomPeriodCancel.bind(this);

    this.DEFAULT_START = props.start || TODAY;
    this.DEFAULT_END = props.end || TODAY;
  }

  // Maps props.options, which are passed directly
  // from the ReportingPeriodEnum, into an array of
  // "option" objects that are consumable by select component
  getParsedOptions() {
    return this.props.options.map((enumObject) => ({
      value: enumObject.slug,
      label: capitalize(enumObject.displayName),
      group: enumObject.group,
      disabled: enumObject.disabled || false,
    }));
  }

  getCustomPeriodDefaults() {
    return {
      start: this.DEFAULT_START,
      end: this.DEFAULT_END,
      customOptionSelectedDisplay: this.getCustomOptionSelectedDisplay(
        this.DEFAULT_START,
        this.DEFAULT_END,
      ),
    };
  }

  getCustomOptionSelectedDisplay(start, end) {
    return formatDateRange(start, end, DateTimeFormat.TEXTUAL);
  }

  render() {
    const groups = this.props.groups;

    let options = this.getParsedOptions();
    if (this.props.includeEmptyPeriod) {
      options.unshift({
        value: NO_PERIOD_OPTION_VALUE,
        label: NO_PERIOD_OPTION_LABEL,
      });
    }
    if (this.props.includeCustomPeriod) {
      if (Object.entries(groups).length) {
        Object.keys(groups).forEach((group) => {
          options = options.concat({
            value: `${group}-${CUSTOM_OPTION_VALUE}`,
            label: CUSTOM_OPTION_LABEL,
            group,
            selectedDisplay: this.state.customOptionSelectedDisplay,
          });
        });
      } else {
        options = options.concat({
          value: CUSTOM_OPTION_VALUE,
          label: CUSTOM_OPTION_LABEL,
          selectedDisplay: this.state.customOptionSelectedDisplay,
        });
      }
    }

    return (
      <span className={'PeriodSelector'}>
        <Select
          groups={groups}
          name={'PeriodSelector'}
          onChange={this.onOptionClick}
          options={options}
          placeholder={NO_PERIOD_OPTION_LABEL}
          showSearch={false}
          value={this.state.selected}
        />
        <ConfirmationModal
          body={
            <div>
              <strong>{t('Start')}</strong>
              <div>
                <DatePicker
                  onChange={this.onChangeStart}
                  selected={this.state.start}
                />
              </div>
              <strong>{t('End')}</strong>
              <div>
                <DatePicker
                  onChange={this.onChangeEnd}
                  selected={this.state.end}
                />
              </div>
            </div>
          }
          buttonProps={{
            type: 'green',
            children: t('Save'),
          }}
          header={t('Custom period')}
          onCancel={this.onCustomPeriodCancel}
          onConfirm={this.onCustomPeriodSave}
          ref={(modal) => (this._modal = modal)}
        />
      </span>
    );
  }

  onChangeEnd(end) {
    this.setState({end});
  }

  onChangeStart(start) {
    this.setState({start});
  }

  onCustomPeriodCancel() {
    this.setState(this.getCustomPeriodDefaults());
  }

  onCustomPeriodSave() {
    this.setState(
      {
        ...this.state,
        customOptionSelectedDisplay: this.getCustomOptionSelectedDisplay(
          this.state.start,
          this.state.end,
        ),
      },
      () => this.props.onSave(this.state),
    );
  }

  onOptionClick(clicked) {
    const option = this.props.options.find(option => option.slug == clicked);
    if (option && option.disabled){
      return;
    }

    if (clicked.includes(CUSTOM_OPTION_VALUE)) {
      this.setState({selected: clicked});
      return this._modal.show();
    }
    this.setState(
      {
        selected: clicked,
        ...this.getCustomPeriodDefaults(),
      },
      () => this.props.onSave(this.state),
    );
  }
}

function triggerNewCustomPeriod(start, end, tz) {
  const queryParams = {
    start: formatDate(start, DateTimeFormat.DATE),
    end: formatDate(end, DateTimeFormat.DATE),
  };
  if (tz) {
    queryParams.tz = tz;
  }
  const toRemove = ['period'];
  triggerQueryParams(queryParams, toRemove);
}

function triggerNewPeriod(periodSlug, tz) {
  const queryParams = {period: periodSlug};
  if (tz) {
    queryParams.tz = tz;
  }
  const toRemove = ['start', 'end'];
  triggerQueryParams(queryParams, toRemove);
}

function triggerNoPeriod() {
  const toRemove = ['period', 'start', 'end', 'tz'];
  triggerQueryParams({}, toRemove);
}

function defaultOnSave(state) {
  const {selected, start, end, tz} = state;

  if (selected === NO_PERIOD_OPTION_VALUE) {
    triggerNoPeriod();
  } else if (selected.includes(CUSTOM_OPTION_VALUE)) {
    triggerNewCustomPeriod(start, end, tz);
  } else {
    triggerNewPeriod(selected, tz);
  }
}
